/**
 * Contact Page Component
 * Displays general contact information, location maps, phone/email details,
 * and dynamically calculates today's opening hours for the Clipstone Miners Welfare Institute.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildMailtoHref, getContactDetails } from '../utils/contactDetails';

const Contact = () => {
  const location = useLocation();
  const [contactDetails, setContactDetails] = useState(() => getContactDetails());
  const trustContact = contactDetails.trust;
  const socialContact = contactDetails.social;
  const todayName = new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(new Date());

  const renderMultilineText = (value) => String(value || '').split(/\r?\n/).map((line, index, lines) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));

  useEffect(() => {
    if (location.hash === '#trust') {
      setTimeout(() => {
        document.getElementById('trust-contacts-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (location.hash === '#social') {
      setTimeout(() => {
        document.getElementById('social-contacts-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const refreshContactDetails = () => {
      setContactDetails(getContactDetails());
    };

    window.addEventListener('contact-details-updated', refreshContactDetails);
    window.addEventListener('storage', refreshContactDetails);

    return () => {
      window.removeEventListener('contact-details-updated', refreshContactDetails);
      window.removeEventListener('storage', refreshContactDetails);
    };
  }, []);

  return (
    <div style={s.page}>

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div style={s.pageHeader}>
        <h1 id="contact-page-heading" style={s.pageTitle}>Contact Us</h1>
        <p style={s.pageSubtitle}>Find the right team for your enquiry below.</p>
        <div style={s.jumpRow} className="jumpRow">
          <a href="#trust" style={s.jumpLink} className="jumpLink">Community Trust</a>
          <a href="#social" style={s.jumpLink} className="jumpLink">Social Club</a>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TRUST SECTION — dark panel with side-by-side layout
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="trust-contacts-section" style={s.trustSection}>
        <div style={s.trustInner} className="trustInner">

          <div style={s.trustLeft}>
            <span style={s.trustKicker}>Community Trust</span>
            <h2 style={s.trustHeading}>Get in touch with the Trust</h2>
            <p style={s.trustLead}>Welfare programmes, heritage projects, and community initiatives.</p>

            <div style={s.trustDetailList}>
              <div style={s.trustDetailRow}>
                <span style={s.trustDetailLabel}>Address</span>
                <span style={s.trustDetailValue}>{renderMultilineText(trustContact.address)}</span>
              </div>
              <div style={s.trustDivider} />
              <div style={s.trustDetailRow}>
                <span style={s.trustDetailLabel}>Phone</span>
                <span style={s.trustDetailValue}>{trustContact.phone}</span>
              </div>
              <div style={s.trustDivider} />
              <div style={s.trustDetailRow}>
                <span style={s.trustDetailLabel}>Email</span>
                <a
                  id="contact-email-link"
                  href={buildMailtoHref(trustContact.email, trustContact.emailSubject, trustContact.emailBody)}
                  style={s.trustEmailLink}
                >
                  {trustContact.email}
                </a>
              </div>
            </div>
          </div>

          <div style={s.trustRight}>
            <div style={s.trustHoursCard}>
              <div style={s.trustHoursHeader}>
                <span style={s.trustHoursTitle}>Opening Hours</span>
                <span style={s.trustHoursToday}>Today: {trustContact.hours.find((h) => h.day === todayName)?.hours || 'Closed'}</span>
              </div>
              <div id="contact-hours-section" style={s.trustHoursList}>
                {trustContact.hours.map(({ day, hours }) => (
                  <div key={day} style={{ ...s.trustHoursRow, ...(day === todayName ? s.trustHoursRowActive : {}) }}>
                    <span style={s.trustHoursDay}>{day}</span>
                    <span style={s.trustHoursTime}>{hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SOCIAL SECTION — light card-grid layout with teal accent
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="social-contacts-section" style={s.socialSection}>
        <div style={s.socialInner} className="socialInner">

          <div style={s.socialHeaderRow}>
            <div>
              <span style={s.socialKicker}>Social Club</span>
              <h2 style={s.socialHeading}>Reach the Social Club</h2>
              <p style={s.socialLead}>Room hire, events, and general social club enquiries.</p>
            </div>
          </div>

          <div style={s.socialCardGrid}>
            <div style={s.socialContactCard}>
              <div style={s.socialContactCardLabel}>Address</div>
              <div style={s.socialContactCardValue}>{renderMultilineText(socialContact.address)}</div>
            </div>
            <div style={s.socialContactCard}>
              <div style={s.socialContactCardLabel}>Phone</div>
              <div style={s.socialContactCardValue}>{socialContact.phone}</div>
            </div>
            <div style={{ ...s.socialContactCard, ...s.socialContactCardWide }}>
              <div style={s.socialContactCardLabel}>Email</div>
              <a
                id="contact-social-email-link"
                href={buildMailtoHref(socialContact.email, socialContact.emailSubject, socialContact.emailBody)}
                style={s.socialEmailLink}
              >
                {socialContact.email}
              </a>
              <p style={s.socialEmailNote}>Click to open your email app</p>
            </div>
          </div>

          <div id="contact-social-hours-section" style={s.socialHoursBlock}>
            <div style={s.socialHoursHeadRow}>
              <span style={s.socialHoursTitle}>Opening Hours</span>
              <span style={s.socialHoursTodayBadge}>
                Today ({todayName}): {socialContact.hours.find((h) => h.day === todayName)?.hours || 'Closed'}
              </span>
            </div>
            <div style={s.socialHoursGrid}>
              {socialContact.hours.map(({ day, hours }) => (
                <div key={day} style={{ ...s.socialHoursPill, ...(day === todayName ? s.socialHoursPillActive : {}) }}>
                  <span style={s.socialHoursPillDay}>{day.slice(0, 3)}</span>
                  <span style={s.socialHoursPillTime}>{hours}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

    </div>
  );
};

/* ─── Styles ─────────────────────────────────────────────────────────────────── */
const s = {

  /* Page wrapper */
  page: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    minHeight: '100vh',
  },

  /* Header */
  pageHeader: {
    textAlign: 'center',
    padding: '56px 24px 40px',
    background: '#ffffff',
  },
  pageTitle: {
    fontSize: 'clamp(28px, 5vw, 44px)',
    fontWeight: '800',
    color: '#111827',
    margin: '0 0 10px',
  },
  pageSubtitle: {
    fontSize: '17px',
    color: '#6b7280',
    margin: '0 0 28px',
  },
  jumpRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  jumpLink: {
    padding: '10px 24px',
    borderRadius: '999px',
    border: '2px solid #1e3f32',
    color: '#1e3f32',
    fontWeight: '700',
    fontSize: '14px',
    textDecoration: 'none',
    letterSpacing: '0.02em',
  },

  /* ── Trust section ─────────────────────────────────────────────────────── */
  trustSection: {
    background: 'linear-gradient(135deg, #0b2318 0%, #123926 60%, #1a4d34 100%)',
    padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 60px)',
  },
  trustInner: {
    maxWidth: '1080px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '48px',
    alignItems: 'start',
  },
  trustLeft: {},
  trustKicker: {
    display: 'inline-block',
    background: 'rgba(74, 222, 128, 0.15)',
    color: '#4ade80',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '4px 12px',
    borderRadius: '999px',
    marginBottom: '14px',
  },
  trustHeading: {
    color: '#ffffff',
    fontSize: 'clamp(22px, 3.5vw, 32px)',
    fontWeight: '800',
    margin: '0 0 10px',
    lineHeight: 1.25,
  },
  trustLead: {
    color: '#86efac',
    fontSize: '15px',
    margin: '0 0 32px',
    lineHeight: 1.6,
  },
  trustDetailList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  trustDetailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '16px 0',
  },
  trustDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.08)',
  },
  trustDetailLabel: {
    color: '#4ade80',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  trustDetailValue: {
    color: '#f0fdf4',
    fontSize: '16px',
    fontWeight: '500',
  },
  trustEmailLink: {
    color: '#f0fdf4',
    fontSize: '15px',
    fontWeight: '600',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(74,222,128,0.5)',
    textUnderlineOffset: '3px',
  },
  trustRight: {},
  trustHoursCard: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '28px',
    backdropFilter: 'blur(8px)',
  },
  trustHoursHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  trustHoursTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '700',
  },
  trustHoursToday: {
    color: '#4ade80',
    fontSize: '13px',
    fontWeight: '600',
  },
  trustHoursList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  trustHoursRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '9px 12px',
    borderRadius: '10px',
    background: 'transparent',
  },
  trustHoursRowActive: {
    background: 'rgba(74, 222, 128, 0.12)',
    border: '1px solid rgba(74, 222, 128, 0.3)',
  },
  trustHoursDay: {
    color: '#d1fae5',
    fontSize: '14px',
  },
  trustHoursTime: {
    color: '#86efac',
    fontSize: '14px',
    fontWeight: '600',
  },

  /* ── Social section ────────────────────────────────────────────────────── */
  socialSection: {
    background: '#f8fafc',
    padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 60px)',
  },
  socialInner: {
    maxWidth: '1080px',
    margin: '0 auto',
  },
  socialHeaderRow: {
    marginBottom: '52px',
  },
  socialKicker: {
    display: 'inline-block',
    background: 'rgba(14, 116, 144, 0.1)',
    color: '#0e7490',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '4px 12px',
    borderRadius: '999px',
    marginBottom: '12px',
  },
  socialHeading: {
    color: '#0f172a',
    fontSize: 'clamp(22px, 3.5vw, 32px)',
    fontWeight: '800',
    margin: '0 0 8px',
    lineHeight: 1.25,
  },
  socialLead: {
    color: '#475569',
    fontSize: '15px',
    margin: 0,
  },
  socialCardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '36px',
  },
  socialContactCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  socialContactCardWide: {
    gridColumn: 'span 1',
  },
  socialContactCardLabel: {
    color: '#0e7490',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '6px',
  },
  socialContactCardValue: {
    color: '#0f172a',
    fontSize: '15px',
    fontWeight: '600',
    lineHeight: 1.5,
  },
  socialEmailLink: {
    color: '#0e7490',
    fontSize: '14px',
    fontWeight: '700',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(14,116,144,0.35)',
    textUnderlineOffset: '3px',
    display: 'block',
    marginBottom: '4px',
  },
  socialEmailNote: {
    color: '#94a3b8',
    fontSize: '12px',
    margin: 0,
  },
  socialHoursBlock: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '20px',
    padding: '28px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  socialHoursHeadRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '20px',
  },
  socialHoursTitle: {
    color: '#0f172a',
    fontSize: '16px',
    fontWeight: '700',
  },
  socialHoursTodayBadge: {
    background: 'rgba(14, 116, 144, 0.08)',
    color: '#0e7490',
    border: '1px solid rgba(14,116,144,0.2)',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '13px',
    fontWeight: '600',
  },
  socialHoursGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '10px',
  },
  socialHoursPill: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    padding: '12px 14px',
    borderRadius: '12px',
    background: '#f1f5f9',
    border: '1px solid transparent',
  },
  socialHoursPillActive: {
    background: 'rgba(14, 116, 144, 0.08)',
    border: '1px solid rgba(14,116,144,0.25)',
  },
  socialHoursPillDay: {
    color: '#64748b',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  socialHoursPillTime: {
    color: '#0f172a',
    fontSize: '13px',
    fontWeight: '600',
  },
};

export default Contact;
